import { useEffect } from "react"

import type { SessionData } from "@productlane/auth"

import { api } from "./api"
import { useAuthState, useAuthStateData } from "./states/auth-state"

declare global {
	interface Window {
		dataLayer: Array<Record<string, unknown>>
	}
}

export function useSession(): {
	session: SessionData | null
	isLoading: boolean
	isLoaded: boolean
} {
	const localAuthData = useAuthStateData()
	const setSessionData = useAuthState((s) => s.setSessionData)
	const userDataQuery = api.user.info.useQuery(undefined, {
		staleTime: Infinity,
		cacheTime: Infinity,
		refetchOnMount: true,
		refetchOnWindowFocus: true,
		refetchOnReconnect: true,
		useErrorBoundary: false,
	})

	useEffect(() => {
		if (userDataQuery.data) {
			setSessionData(userDataQuery.data)
			// these classes are for google analytics conversion tracking
			document.body.classList.add("productlane-user")
			window.dataLayer = window.dataLayer || []
			// Find an existing entry with user_id in the data layer
			const existingEntry = window.dataLayer.find(
				(entry) => entry.user_id !== undefined,
			)
			const userId = userDataQuery.data.userId
			if (existingEntry && existingEntry?.user_id !== userId) {
				existingEntry.user_id = userId
			} else if (!existingEntry) {
				window.dataLayer.push({
					user_id: userId,
				})
			}

			if (userDataQuery.data.workspaceId) {
				document.body.classList.add("productlane-workspace")
			}
		} else if (userDataQuery.data === null) {
			setSessionData(null)
		}
	}, [setSessionData, userDataQuery.data])
	return localAuthData
		? {
				session: localAuthData,
				isLoading: userDataQuery.isLoading,
				isLoaded: userDataQuery.isSuccess,
			}
		: {
				session: null,
				isLoading: userDataQuery.isLoading,
				isLoaded: userDataQuery.isSuccess,
			}
}
